// src/components/CaseSelector.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import caseContent from "../caseStudyContents"; // rich JSX/HTML per case id
import metaList from "../caseStudies";          // [{ id, name, image, category }, ...]

/* ---------- tiny utilities ---------- */

// Pull plain text out of JSX/React nodes (recursively)
function nodeToText(node) {
  if (node == null || node === false) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join("\n");
  if (React.isValidElement(node)) return nodeToText(node.props?.children);
  return "";
}

// Parse "Assistant/Subject" labelled text into [{role,text}]
const LABEL_RE = /^(Assistant(?:\s+Agent)?|Assistant|Subject):\s*(.*)$/i;
function parseConversation(text) {
  if (typeof text !== "string") return null;
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const msgs = [];
  let cur = null;

  for (const raw of lines) {
    const m = raw.match(LABEL_RE);
    if (m) {
      if (cur) msgs.push(cur);
      const role = m[1].toLowerCase().includes("subject") ? "subject" : "assistant";
      cur = { role, text: m[2] || "" };
    } else if (cur) {
      cur.text += (cur.text ? "\n" : "") + raw;
    }
  }
  if (cur) msgs.push(cur);
  return msgs.length ? msgs : null;
}

// minimal chat CSS (scoped)
const chatCSS = `
  .gchat{display:flex;flex-direction:column;gap:10px}
  .gchat-row{display:flex}
  .gchat-row.assistant{justify-content:flex-start}
  .gchat-row.subject{justify-content:flex-end}
  .bubble{max-width:72%;padding:10px 12px;border-radius:14px;border:1px solid rgba(0,0,0,.06);
          box-shadow:0 1px 2px rgba(0,0,0,.04);white-space:pre-wrap;line-height:1.55}
  .bubble.assistant{background:#eef2ff;border-color:#c7d2fe;color:#1e40af}
  .bubble.subject{background:#ecfdf5;border-color:#a7f3d0;color:#065f46}
  .who{display:block;font-weight:600;font-size:.85rem;opacity:.85;margin-bottom:4px}
`;

function ChatBubbles({ value }) {
  // Accept string OR JSX; convert JSX to text first
  const text =
    typeof value === "string" ? value : React.isValidElement(value) ? nodeToText(value) : "";

  const msgs = parseConversation(text);
  if (!msgs) {
    // fallback: raw with line breaks if it doesn't match the label format
    return <div className="content" style={{ whiteSpace: "pre-wrap" }}>{text || String(value)}</div>;
  }
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: chatCSS }} />
      <div className="gchat">
        {msgs.map((m, i) => (
          <div key={i} className={`gchat-row ${m.role}`}>
            <div className={`bubble ${m.role}`}>
              <span className="who">{m.role === "assistant" ? "Assistant" : "Subject"}</span>
              {m.text}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/* ---------- component ---------- */
const TAB_ORDER = [
  "Personalization and Social Framing",
  "Proactivity and Context-Awareness",
  "Managing Attention and Interruptibility",
  "User Control and In-Situ Configuration",
];


export default function CaseSelector() {
  const { hash } = useLocation();

  // index meta by id
  const metaById = useMemo(
    () => Object.fromEntries((metaList || []).map((m) => [m.id, m])),
    []
  );

  // group ids by category (order by TAB_ORDER first, then alphabetical leftovers)
  const categories = useMemo(() => {
    const map = new Map(); // category -> ids[]
    (metaList || []).forEach(({ id, category }) => {
      if (!caseContent[id]) return; // only cases that have content
      if (!map.has(category)) map.set(category, []);
      map.get(category).push(id);
    });
    const ordered = [];
    TAB_ORDER.forEach((label) => {
      if (map.has(label)) ordered.push({ label, cases: map.get(label) });
    });
    [...map.keys()]
      .filter((k) => !TAB_ORDER.includes(k))
      .sort()
      .forEach((label) => ordered.push({ label, cases: map.get(label) }));
    return ordered;
  }, []);

  // defaults
  const firstCat = categories[0]?.label || "";
  const firstId = categories[0]?.cases?.[0] || null;

  const [activeCat, setActiveCat] = useState(firstCat);
  const [selected, setSelected] = useState(firstId);

  // allow deep-link #cs7
  useEffect(() => {
    const m = hash.match(/^#(cs\d{1,2})$/i);
    const id = m?.[1]?.toLowerCase();
    if (id && caseContent[id]) {
      setSelected(id);
      const cat = metaById[id]?.category;
      if (cat) setActiveCat(cat);
    }
  }, [hash, metaById]);

  const visibleIds = useMemo(() => {
    const row = categories.find((c) => c.label === activeCat);
    return row ? row.cases.filter((id) => caseContent[id]) : [];
  }, [activeCat, categories]);

  const selMeta = selected ? metaById[selected] : null;
  const selData = selected ? caseContent[selected] : null;

  return (
    <div className="content">
      {/* Category tabs (custom flexbox layout for proper wrapping) */}
      <div style={{ marginBottom: "0" }}>
        <style>
          {`
            .button-custom-size {
              font-size: 0.875rem !important;
              padding: 0.5em 0.75em !important;
              height: auto !important;
            }
          `}
        </style>
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", justifyContent: "center", backgroundColor: '#888888', padding: '20px', borderRadius: '8px 8px 0 0' }}>
          {categories.map((cat) => (
            <button
              key={cat.label}
              type="button"
              className={`button button-custom-size ${activeCat === cat.label ? "is-primary" : "is-light"}`}
              onClick={() => {
                setActiveCat(cat.label);
                const first = cat.cases.find((id) => caseContent[id]);
                if (first) setSelected(first);
              }}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Case id buttons */}
      <div className="buttons is-centered is-flex is-flex-wrap-wrap" style={{backgroundColor: '#888888', padding: '20px', borderRadius: '0 0 8px 8px'}}>
        {visibleIds.map((id) => (
          <button
            key={id}
            type="button"
            className={`button is-small ${selected === id ? "is-link" : "is-light"}`}
            onClick={() => setSelected(id)}
            title={metaById[id]?.name || id.toUpperCase()}
          >
            {id.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Selected case name UNDER the CS buttons */}
      {selMeta && (
        <div className="has-text-centered mb-3">
          <h3 className="title is-5">{selMeta.name}</h3>
          {selMeta.category && (
            <p className="is-size-7 has-text-grey">{selMeta.category}</p>
          )}
        </div>
      )}

      {/* Detail panels */}
      {selData && (
        <div className="columns mt-4">
          {/* LEFT column: 3 panels */}
          <div className="column is-half">
            {/* 1) User Profile */}
            {selData.userProfile && (
              <div className="box" style={{ marginBottom: "1rem" }}>
                <p className="title is-6">User Profile</p>
                <div className="content">
                  {React.isValidElement(selData.userProfile) ? (
                    selData.userProfile
                  ) : (
                    <div dangerouslySetInnerHTML={{ __html: String(selData.userProfile) }} />
                  )}
                </div>
              </div>
            )}

            {/* 2) System Overview (keep only inner headings from content) */}
            {selData.systemOverview && (
              <div className="box" style={{ marginBottom: "1rem" }}>
                <div className="content">
                  {React.isValidElement(selData.systemOverview) ? (
                    selData.systemOverview
                  ) : (
                    <div dangerouslySetInnerHTML={{ __html: String(selData.systemOverview) }} />
                  )}
                </div>
              </div>
            )}

            {/* 3) Conversation / Transcript */}
            {selData.conversation && (
              
              <div className="box" style={{ marginBottom: "1rem" }}>
                <p className="title is-6">Conversation</p>
                <ChatBubbles value={selData.conversation} />
              </div>
            )}
          </div>

          {/* RIGHT column: 2 panels */}
          <div className="column is-half">
            {/* 4) Detailed Activities */}
            {selData.detailedActivities && (
              <div className="box" style={{ marginBottom: "1rem" }}>
                <p className="title is-6">Detailed Activities</p>
                <div className="content">
                  {React.isValidElement(selData.detailedActivities) ? (
                    selData.detailedActivities
                  ) : (
                    <div
                      dangerouslySetInnerHTML={{ __html: String(selData.detailedActivities) }}
                    />
                  )}
                </div>
              </div>
            )}

            {/* 5) Extended Findings with GIDEA (keep only inner headings from content) */}
            {selData.gideaReplication && (
              <div className="box" style={{ marginBottom: "1rem" }}>
                <div className="content">
                  {React.isValidElement(selData.gideaReplication) ? (
                    selData.gideaReplication
                  ) : (
                    <div dangerouslySetInnerHTML={{ __html: String(selData.gideaReplication) }} />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

/* ---------- small chat-bubbles renderer ---------- */


